// Entités de la maquette : champs, libellés, types, listes de valeurs, références. Extraits du HTML de référence.
// Les fonctions (display, filter, default) du HTML sont portées dans utils/entityFns.ts, sous le même nom de champ.

export interface FieldDef {
  key: string
  label: string
  type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'fk' | 'multiselect' | 'image'
  required: boolean
  ref: string | null
  options: string[] | null
  placeholder: string | null
  /** Clé de la propriété affichée quand `display` est une chaîne dans la maquette. */
  display: string | null
  /** La maquette utilise une fonction pour `display` / `filter` / `default` : voir utils/entityFns.ts. */
  displayFn: boolean
  filterFn: boolean
  virtual: boolean
  cascadeClasse: boolean
  default: string | number | boolean | null
  defaultFn: boolean
  computeValue: boolean
}

export interface EntityDef {
  label: string
  labelPlural: string
  module: string
  icon: string
  storageKey: string | null
  fields: FieldDef[]
  extraFilterKeys: string[]
}

export const ENTITY_DEFS: Record<string, EntityDef> = {
 "ecoles": {
  "fields": [
   {
    "key": "code",
    "label": "Code",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. ISCA-ABJ",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "logo",
    "label": "Logo de l'école",
    "type": "image",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "sigle",
    "label": "Sigle",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. ISCA",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Institut Supérieur de Commerce d’Abidjan",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "active",
    "label": "École active",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": true,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "ville",
    "label": "Ville / commune",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Abidjan, Cocody",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "adresse",
    "label": "Adresse complète",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Route de l’université, boulevard F. Mittérrant",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "telephone",
    "label": "Téléphone",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 22 48 48 12",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "fax",
    "label": "Fax",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 22 48 48 14",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "siteWeb",
    "label": "Site web",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. www.monecole.ci",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "email",
    "label": "E-mail",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. contact@monecole.ci",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "agrementMinisteriel",
    "label": "Agrément ministériel (pour le relevé de notes)",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. N° 000 MESRS/DGESIP/DESUP du 00 mois 0000",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "nomClientBanque",
    "label": "Nom du client (relevés bancaires)",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. ISCA",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "versementBanque",
    "label": "Banque de versement",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. COFINA",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "versementCompte",
    "label": "Compte de versement",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 089 160 000 001",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "virementBanque",
    "label": "Banque de virement",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. BDA",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "virementRib",
    "label": "RIB de virement",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. CI201 01001 109 708 379 387 32",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "École",
  "labelPlural": "Écoles",
  "module": "referentiel",
  "icon": "building-2",
  "storageKey": null
 },
 "annees": {
  "fields": [
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 2025-2026",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateDebut",
    "label": "Date de début",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateFin",
    "label": "Date de fin",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "statut",
    "label": "Statut",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "PREPARATION",
     "OUVERTE",
     "CLOTUREE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "estCourante",
    "label": "Année courante",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Année académique",
  "labelPlural": "Années académiques",
  "module": "referentiel",
  "icon": "calendar",
  "storageKey": null
 },
 "cycles": {
  "fields": [
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Premier cycle (Licence / BTS)",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "ordre",
    "label": "Ordre",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 1",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Cycle",
  "labelPlural": "Cycles",
  "module": "offre",
  "icon": "repeat",
  "storageKey": null
 },
 "offres": {
  "fields": [
   {
    "key": "code",
    "label": "Code",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. OFR-MKG",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Licence Marketing Management",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "active",
    "label": "Offre active",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": true,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dureeAnnees",
    "label": "Durée (années)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 3",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "diplomePrepare",
    "label": "Diplôme préparé",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Licence",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "niveauIds",
    "label": "Niveaux rattachés",
    "type": "multiselect",
    "required": false,
    "ref": "niveaux",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": true,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": true
   }
  ],
  "extraFilterKeys": [],
  "label": "Offre de formation",
  "labelPlural": "Offres de formation",
  "module": "offre",
  "icon": "graduation-cap",
  "storageKey": null
 },
 "niveaux": {
  "fields": [
   {
    "key": "code",
    "label": "Code",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. A1",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 1ère année",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "cycleId",
    "label": "Cycle",
    "type": "fk",
    "required": true,
    "ref": "cycles",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "ordre",
    "label": "Ordre",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 1",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "moyennePassage",
    "label": "Moyenne de passage (/20)",
    "type": "number",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 10",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Niveau",
  "labelPlural": "Niveaux",
  "module": "offre",
  "icon": "layers",
  "storageKey": null
 },
 "niveauOffre": {
  "fields": [
   {
    "key": "offreId",
    "label": "Offre de formation",
    "type": "fk",
    "required": true,
    "ref": "offres",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "niveauId",
    "label": "Niveau",
    "type": "fk",
    "required": true,
    "ref": "niveaux",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelleLocal",
    "label": "Libellé local (optionnel)",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Licence 1",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "volumeHoraire",
    "label": "Volume horaire",
    "type": "number",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 600",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "ordreDansOffre",
    "label": "Ordre dans la formation",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 1",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Niveau de formation",
  "labelPlural": "Niveaux par formation",
  "module": "offre",
  "icon": "compass",
  "storageKey": null
 },
 "classes": {
  "fields": [
   {
    "key": "code",
    "label": "Code",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. MKG-A1-A",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Licence 1 Marketing Management A",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "offreId",
    "label": "Offre de formation",
    "type": "fk",
    "required": true,
    "ref": "offres",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "niveauId",
    "label": "Niveau",
    "type": "fk",
    "required": true,
    "ref": "niveaux",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "anneeId",
    "label": "Année académique",
    "type": "fk",
    "required": true,
    "ref": "annees",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": true,
    "computeValue": false
   },
   {
    "key": "capaciteMax",
    "label": "Capacité max.",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 40",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "salle",
    "label": "Salle",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Amphi A",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "professeurPrincipalId",
    "label": "Professeur principal",
    "type": "fk",
    "required": false,
    "ref": "personnes",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": true,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Classe",
  "labelPlural": "Classes",
  "module": "offre",
  "icon": "tag",
  "storageKey": null
 },
 "personnes": {
  "fields": [
   {
    "key": "nom",
    "label": "Nom",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Kouassi",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "prenoms",
    "label": "Prénoms",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Aïcha",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "sexe",
    "label": "Sexe",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "MASCULIN",
     "FEMININ"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateNaissance",
    "label": "Date de naissance",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "lieuNaissance",
    "label": "Lieu de naissance",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Abidjan",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "nationalite",
    "label": "Nationalité",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Ivoirienne",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "telephone",
    "label": "Téléphone",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 07 48 51 22 36",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "email",
    "label": "Email",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. aicha.kouassi@example.com",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "role",
    "label": "Rôle",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "ETUDIANT",
     "ENSEIGNANT",
     "PARENT",
     "PERSONNEL_ADMIN"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Personne",
  "labelPlural": "Personnes",
  "module": "personnes",
  "icon": "user",
  "storageKey": null
 },
 "liensParentaux": {
  "fields": [
   {
    "key": "etudiantId",
    "label": "Étudiant",
    "type": "fk",
    "required": true,
    "ref": "personnes",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": true,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "parentId",
    "label": "Parent",
    "type": "fk",
    "required": true,
    "ref": "personnes",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": true,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "typeLien",
    "label": "Type de lien",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "PERE",
     "MERE",
     "TUTEUR",
     "AUTRE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "estPayeur",
    "label": "Payeur",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "estContactUrgence",
    "label": "Contact d'urgence",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "ordrePriorite",
    "label": "Ordre de priorité",
    "type": "number",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 1",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Lien parental",
  "labelPlural": "Liens parentaux",
  "module": "personnes",
  "icon": "link",
  "storageKey": null
 },
 "inscriptions": {
  "fields": [
   {
    "key": "numero",
    "label": "Numéro",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "généré automatiquement si vide",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "etudiantId",
    "label": "Étudiant",
    "type": "fk",
    "required": true,
    "ref": "personnes",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": true,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "classeId",
    "label": "Classe",
    "type": "fk",
    "required": false,
    "ref": "classes",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "anneeId",
    "label": "Année académique",
    "type": "fk",
    "required": true,
    "ref": "annees",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": true,
    "computeValue": false
   },
   {
    "key": "typeInscription",
    "label": "Type d'inscription",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "NOUVELLE",
     "REINSCRIPTION",
     "TRANSFERT"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "statut",
    "label": "Statut",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "BROUILLON",
     "EN_ATTENTE_VALIDATION",
     "EN_ATTENTE_PAIEMENT",
     "VALIDEE",
     "ANNULEE",
     "ABANDON",
     "TRANSFEREE",
     "TERMINEE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateDemande",
    "label": "Date de demande",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "moyenneGenerale",
    "label": "Moyenne générale (/20)",
    "type": "number",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 14.5",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Inscription",
  "labelPlural": "Inscriptions",
  "module": "inscription",
  "icon": "clipboard-list",
  "storageKey": null
 },
 "historiqueStatutInscription": {
  "fields": [
   {
    "key": "inscriptionId",
    "label": "Inscription",
    "type": "fk",
    "required": true,
    "ref": "inscriptions",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "statutPrecedent",
    "label": "Statut précédent",
    "type": "select",
    "required": false,
    "ref": null,
    "options": [
     "BROUILLON",
     "EN_ATTENTE_VALIDATION",
     "EN_ATTENTE_PAIEMENT",
     "VALIDEE",
     "ANNULEE",
     "ABANDON",
     "TRANSFEREE",
     "TERMINEE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "statutNouveau",
    "label": "Nouveau statut",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "BROUILLON",
     "EN_ATTENTE_VALIDATION",
     "EN_ATTENTE_PAIEMENT",
     "VALIDEE",
     "ANNULEE",
     "ABANDON",
     "TRANSFEREE",
     "TERMINEE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateChangement",
    "label": "Date du changement",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "aaaa-mm-jjThh:mm:ss.sssZ",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "motif",
    "label": "Motif",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Dossier complet, transmis à la Caisse",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Historique de statut",
  "labelPlural": "Historiques de statut",
  "module": "inscription",
  "icon": "history",
  "storageKey": null
 },
 "tranches": {
  "fields": [
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Octobre",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateEcheance",
    "label": "Date d'échéance",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "ordre",
    "label": "Ordre",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 1",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "anneeId",
    "label": "Année académique",
    "type": "fk",
    "required": true,
    "ref": "annees",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Tranche",
  "labelPlural": "Calendrier des tranches",
  "module": "finance",
  "icon": "calendar-days",
  "storageKey": null
 },
 "tarifications": {
  "fields": [
   {
    "key": "code",
    "label": "Code",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. SCOL",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Frais de scolarité",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "obligatoire",
    "label": "Obligatoire",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "recurrent",
    "label": "Récurrent",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "cycleId",
    "label": "Cycle",
    "type": "fk",
    "required": true,
    "ref": "cycles",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "niveauId",
    "label": "Niveau",
    "type": "fk",
    "required": true,
    "ref": "niveaux",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "anneeId",
    "label": "Année académique",
    "type": "fk",
    "required": true,
    "ref": "annees",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": true,
    "computeValue": false
   },
   {
    "key": "montant",
    "label": "Montant (F CFA)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 650000",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "nombreTranches",
    "label": "Nombre de tranches",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 3",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Tarification",
  "labelPlural": "Tarifications",
  "module": "finance",
  "icon": "tag",
  "storageKey": null
 },
 "reductions": {
  "fields": [
   {
    "key": "code",
    "label": "Code",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. RED-MERITE20",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Bourse mérite",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "active",
    "label": "Active",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": true,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "motif",
    "label": "Motif",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "BOURSE",
     "FRATRIE",
     "MERITE",
     "PERSONNEL",
     "SOCIAL"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "type",
    "label": "Type",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "POURCENTAGE",
     "MONTANT_FIXE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "valeur",
    "label": "Valeur (% ou F CFA selon le type)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 20",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "cumulable",
    "label": "Cumulable",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Réduction",
  "labelPlural": "Réductions",
  "module": "finance",
  "icon": "ticket",
  "storageKey": null
 },
 "reductionsAppliquees": {
  "fields": [
   {
    "key": "inscriptionId",
    "label": "Étudiant / Inscription",
    "type": "fk",
    "required": true,
    "ref": "inscriptions",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": true,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "reductionId",
    "label": "Réduction",
    "type": "fk",
    "required": true,
    "ref": "reductions",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "montantCalcule",
    "label": "Montant calculé (F CFA)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 130000",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateApplication",
    "label": "Date d'application",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "justificatif",
    "label": "Justificatif",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Bourse mérite, dossier académique 2024-2025",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Réduction appliquée",
  "labelPlural": "Réductions appliquées",
  "module": "finance",
  "icon": "banknote",
  "storageKey": null
 },
 "echeanciers": {
  "fields": [
   {
    "key": "inscriptionId",
    "label": "Étudiant / Inscription",
    "type": "fk",
    "required": true,
    "ref": "inscriptions",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": true,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "montantBrut",
    "label": "Montant brut (F CFA)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 650000",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "montantRemise",
    "label": "Remise (F CFA)",
    "type": "number",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 0",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "montantNet",
    "label": "Montant net (F CFA)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 650000",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "statut",
    "label": "Statut",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "A_JOUR",
     "EN_RETARD",
     "SOLDE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Échéancier",
  "labelPlural": "Échéanciers",
  "module": "finance",
  "icon": "calendar-days",
  "storageKey": null
 },
 "lignesEcheance": {
  "fields": [
   {
    "key": "echeancierId",
    "label": "Échéancier",
    "type": "fk",
    "required": true,
    "ref": "echeanciers",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "numeroTranche",
    "label": "N° tranche",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 1",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Tranche 1/3",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "montantDu",
    "label": "Montant dû (F CFA)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 216667",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "montantPaye",
    "label": "Montant payé (F CFA)",
    "type": "number",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 0",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateEcheance",
    "label": "Date d'échéance",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "statut",
    "label": "Statut",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "ATTENDU",
     "PARTIEL",
     "PAYE",
     "EN_RETARD"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Ligne d'échéance",
  "labelPlural": "Lignes d'échéance",
  "module": "finance",
  "icon": "file-text",
  "storageKey": null
 },
 "versements": {
  "fields": [
   {
    "key": "numero",
    "label": "Numéro",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "généré automatiquement si vide",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "inscriptionId",
    "label": "Étudiant / Inscription",
    "type": "fk",
    "required": true,
    "ref": "inscriptions",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": true,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "tarificationId",
    "label": "Type de frais réglé",
    "type": "fk",
    "required": true,
    "ref": "tarifications",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "montant",
    "label": "Montant (F CFA)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 75000",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "datePaiement",
    "label": "Date de paiement",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "modePaiement",
    "label": "Mode de paiement",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "ESPECES",
     "MOBILE_MONEY",
     "VIREMENT",
     "CHEQUE",
     "CARTE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "statut",
    "label": "Statut",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "ENREGISTRE",
     "ANNULE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Versement",
  "labelPlural": "Versements",
  "module": "finance",
  "icon": "credit-card",
  "storageKey": null
 },
 "utilisateurs": {
  "fields": [
   {
    "key": "login",
    "label": "Identifiant",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. aicha.kouassi",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "personneId",
    "label": "Personne liée",
    "type": "fk",
    "required": true,
    "ref": "personnes",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "actif",
    "label": "Compte actif",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": true,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Utilisateur",
  "labelPlural": "Utilisateurs",
  "module": "securite",
  "icon": "lock",
  "storageKey": null
 },
 "roles": {
  "fields": [
   {
    "key": "code",
    "label": "Code",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. ROLE_CAISSIER",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Caissier",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "permissionIds",
    "label": "Permissions",
    "type": "multiselect",
    "required": true,
    "ref": "permissions",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Rôle",
  "labelPlural": "Rôles",
  "module": "securite",
  "icon": "key",
  "storageKey": null
 },
 "permissions": {
  "fields": [
   {
    "key": "code",
    "label": "Code",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. FINANCE_READ",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Consulter, Finance",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "module",
    "label": "Module concerné",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "referentiel",
     "offre",
     "personnes",
     "inscription",
     "finance",
     "securite",
     "pedagogie"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Permission",
  "labelPlural": "Permissions",
  "module": "securite",
  "icon": "key-round",
  "storageKey": null
 },
 "utilisateurRoles": {
  "fields": [
   {
    "key": "utilisateurId",
    "label": "Utilisateur",
    "type": "fk",
    "required": true,
    "ref": "utilisateurs",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "roleId",
    "label": "Rôle",
    "type": "fk",
    "required": true,
    "ref": "roles",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "ecoleId",
    "label": "École (laisser vide pour un accès global)",
    "type": "fk",
    "required": false,
    "ref": "ecoles",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Attribution de rôle",
  "labelPlural": "Attributions de rôle",
  "module": "securite",
  "icon": "badge-check",
  "storageKey": null
 },
 "unitesEnseignement": {
  "fields": [
   {
    "key": "code",
    "label": "Code",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "généré automatiquement si vide (ex. UE1)",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Intitulé / Nature",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Fondamentaux de gestion",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "offreIds",
    "label": "Offres de formation",
    "type": "multiselect",
    "required": true,
    "ref": "offres",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "niveauIds",
    "label": "Niveaux",
    "type": "multiselect",
    "required": true,
    "ref": "niveaux",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "periodeIds",
    "label": "Périodes (semestres)",
    "type": "multiselect",
    "required": true,
    "ref": "periodes",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": true,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Unité d'enseignement",
  "labelPlural": "Unités d'enseignement",
  "module": "pedagogie",
  "icon": "layers",
  "storageKey": null
 },
 "matieres": {
  "fields": [
   {
    "key": "code",
    "label": "Code",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. MAR101",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Marketing fondamental",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "ueId",
    "label": "Unité d'enseignement (UE)",
    "type": "fk",
    "required": false,
    "ref": "unitesEnseignement",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "offreIds",
    "label": "Offres de formation",
    "type": "multiselect",
    "required": true,
    "ref": "offres",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "niveauIds",
    "label": "Niveaux",
    "type": "multiselect",
    "required": true,
    "ref": "niveaux",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "periodeIds",
    "label": "Périodes",
    "type": "multiselect",
    "required": true,
    "ref": "periodes",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": true,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "coefficient",
    "label": "Coefficient",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 2",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "credits",
    "label": "Crédits (ECUE)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 4",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Matière (ECUE)",
  "labelPlural": "Matières (ECUE)",
  "module": "pedagogie",
  "icon": "book",
  "storageKey": null
 },
 "notes": {
  "fields": [
   {
    "key": "inscriptionId",
    "label": "Étudiant / Inscription",
    "type": "fk",
    "required": true,
    "ref": "inscriptions",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": true,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "matiereId",
    "label": "Matière",
    "type": "fk",
    "required": true,
    "ref": "matieres",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "periodeId",
    "label": "Période",
    "type": "fk",
    "required": true,
    "ref": "periodes",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "typeEvaluation",
    "label": "Type d'évaluation",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "DEVOIR",
     "COMPOSITION",
     "INTERROGATION",
     "TP"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "valeur",
    "label": "Note (/20)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 14.5",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateEvaluation",
    "label": "Date d'évaluation",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [
   "classeId",
   "niveauId"
  ],
  "label": "Note",
  "labelPlural": "Notes",
  "module": "pedagogie",
  "icon": "pencil-line",
  "storageKey": null
 },
 "bulletins": {
  "fields": [
   {
    "key": "inscriptionId",
    "label": "Étudiant / Inscription",
    "type": "fk",
    "required": true,
    "ref": "inscriptions",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": true,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "periodeId",
    "label": "Période",
    "type": "fk",
    "required": true,
    "ref": "periodes",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "appreciation",
    "label": "Appréciation",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Bon semestre, à poursuivre.",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateEmission",
    "label": "Date d'émission",
    "type": "date",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [
   "classeId",
   "niveauId"
  ],
  "label": "Bulletin",
  "labelPlural": "Bulletins",
  "module": "pedagogie",
  "icon": "file-check",
  "storageKey": null
 },
 "periodes": {
  "fields": [
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 1er Semestre",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "type",
    "label": "Type",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "TRIMESTRE",
     "SEMESTRE",
     "QUADRIMESTRE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "ordre",
    "label": "Ordre",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 1",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "anneeId",
    "label": "Année académique",
    "type": "fk",
    "required": true,
    "ref": "annees",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": true,
    "computeValue": false
   },
   {
    "key": "dateDebut",
    "label": "Date de début",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateFin",
    "label": "Date de fin",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Période",
  "labelPlural": "Périodes",
  "module": "referentiel",
  "icon": "calendar-days",
  "storageKey": null
 },
 "typesDocument": {
  "fields": [
   {
    "key": "code",
    "label": "Code",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. DOC-CNI",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "libelle",
    "label": "Libellé",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Copie CNI",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "offreIds",
    "label": "Offres de formation",
    "type": "multiselect",
    "required": true,
    "ref": "offres",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "niveauIds",
    "label": "Niveaux",
    "type": "multiselect",
    "required": true,
    "ref": "niveaux",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "obligatoire",
    "label": "Obligatoire",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "formatAccepte",
    "label": "Format accepté",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. PDF, JPG",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Type de document",
  "labelPlural": "Types de document",
  "module": "inscription",
  "icon": "clipboard",
  "storageKey": null
 },
 "documentsDossier": {
  "fields": [
   {
    "key": "inscriptionId",
    "label": "Étudiant / Inscription",
    "type": "fk",
    "required": true,
    "ref": "inscriptions",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": true,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "typeDocumentId",
    "label": "Type de document",
    "type": "fk",
    "required": true,
    "ref": "typesDocument",
    "options": null,
    "placeholder": null,
    "display": "libelle",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "statut",
    "label": "Statut",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "ATTENDU",
     "DEPOSE",
     "VALIDE",
     "REJETE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateDepot",
    "label": "Date de dépôt",
    "type": "date",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "motifRejet",
    "label": "Motif de rejet",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Document illisible",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Document du dossier",
  "labelPlural": "Documents du dossier",
  "module": "inscription",
  "icon": "folder",
  "storageKey": null
 },
 "cartesEtudiantes": {
  "fields": [
   {
    "key": "numeroCarte",
    "label": "Numéro de carte",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "généré automatiquement si vide",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "inscriptionId",
    "label": "Étudiant / Inscription",
    "type": "fk",
    "required": true,
    "ref": "inscriptions",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateEmission",
    "label": "Date d'émission",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateExpiration",
    "label": "Date d'expiration",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "statut",
    "label": "Statut",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "ACTIVE",
     "PERDUE",
     "EXPIREE",
     "DESACTIVEE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Carte étudiante",
  "labelPlural": "Cartes étudiantes",
  "module": "inscription",
  "icon": "id-card",
  "storageKey": null
 },
 "recus": {
  "fields": [
   {
    "key": "numero",
    "label": "Numéro",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "généré automatiquement si vide",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "versementId",
    "label": "Versement",
    "type": "fk",
    "required": true,
    "ref": "versements",
    "options": null,
    "placeholder": null,
    "display": "numero",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "montant",
    "label": "Montant (F CFA)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 75000",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateEmission",
    "label": "Date d'émission",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "annule",
    "label": "Annulé",
    "type": "boolean",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Reçu",
  "labelPlural": "Reçus",
  "module": "finance",
  "icon": "receipt",
  "storageKey": null
 },
 "imputations": {
  "fields": [
   {
    "key": "versementId",
    "label": "Versement",
    "type": "fk",
    "required": true,
    "ref": "versements",
    "options": null,
    "placeholder": null,
    "display": "numero",
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "ligneEcheanceId",
    "label": "Ligne d'échéance",
    "type": "fk",
    "required": true,
    "ref": "lignesEcheance",
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": true,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "montantImpute",
    "label": "Montant imputé (F CFA)",
    "type": "number",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. 75000",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateImputation",
    "label": "Date d'imputation",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Imputation",
  "labelPlural": "Imputations",
  "module": "finance",
  "icon": "calculator",
  "storageKey": null
 },
 "etudiants": {
  "fields": [
   {
    "key": "nom",
    "label": "Nom",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Kouassi",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "prenoms",
    "label": "Prénoms",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Aïcha",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "sexe",
    "label": "Sexe",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "MASCULIN",
     "FEMININ"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateNaissance",
    "label": "Date de naissance",
    "type": "date",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "numeroEtudiant",
    "label": "Numéro d'étudiant",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateEntree",
    "label": "Date d'entrée",
    "type": "date",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "telephone",
    "label": "Téléphone",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 07 48 51 22 36",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "email",
    "label": "Email",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. aicha.kouassi@example.com",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Étudiant",
  "labelPlural": "Étudiants",
  "module": "personnes",
  "icon": "backpack",
  "storageKey": "personnes"
 },
 "enseignants": {
  "fields": [
   {
    "key": "nom",
    "label": "Nom",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Kouassi",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "prenoms",
    "label": "Prénoms",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Aïcha",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "sexe",
    "label": "Sexe",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "MASCULIN",
     "FEMININ"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "specialite",
    "label": "Spécialité",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Marketing digital",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "typeContrat",
    "label": "Type de contrat",
    "type": "select",
    "required": false,
    "ref": null,
    "options": [
     "PERMANENT",
     "VACATAIRE",
     "STAGIAIRE"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "dateEmbauche",
    "label": "Date d'embauche",
    "type": "date",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "telephone",
    "label": "Téléphone",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 07 48 51 22 36",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "email",
    "label": "Email",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. aicha.kouassi@example.com",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Enseignant",
  "labelPlural": "Enseignants",
  "module": "personnes",
  "icon": "user-check",
  "storageKey": "personnes"
 },
 "parents": {
  "fields": [
   {
    "key": "nom",
    "label": "Nom",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Kouassi",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "prenoms",
    "label": "Prénoms",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Bakary",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "sexe",
    "label": "Sexe",
    "type": "select",
    "required": true,
    "ref": null,
    "options": [
     "MASCULIN",
     "FEMININ"
    ],
    "placeholder": null,
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "profession",
    "label": "Profession",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Commerçant",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "employeur",
    "label": "Employeur",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Auto-entrepreneur",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "telephone",
    "label": "Téléphone",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 07 00 00 00 00",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "email",
    "label": "Email",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. bakary.kouassi@example.com",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Parent",
  "labelPlural": "Parents",
  "module": "personnes",
  "icon": "users",
  "storageKey": "personnes"
 },
 "personnelAdmin": {
  "fields": [
   {
    "key": "nom",
    "label": "Nom",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Kouassi",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "prenoms",
    "label": "Prénoms",
    "type": "text",
    "required": true,
    "ref": null,
    "options": null,
    "placeholder": "ex. Bakary",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "fonction",
    "label": "Fonction",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Secrétaire",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "service",
    "label": "Service",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. Scolarité",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "telephone",
    "label": "Téléphone",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. 07 00 00 00 00",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   },
   {
    "key": "email",
    "label": "Email",
    "type": "text",
    "required": false,
    "ref": null,
    "options": null,
    "placeholder": "ex. bakary.kouassi@example.com",
    "display": null,
    "displayFn": false,
    "filterFn": false,
    "virtual": false,
    "cascadeClasse": false,
    "default": null,
    "defaultFn": false,
    "computeValue": false
   }
  ],
  "extraFilterKeys": [],
  "label": "Personnel administratif",
  "labelPlural": "Personnel administratif",
  "module": "personnes",
  "icon": "archive",
  "storageKey": "personnes"
 }
}

export const STATUT_BADGE: Record<string, string> = {
 "VALIDEE": "b-success",
 "ENREGISTRE": "b-success",
 "OUVERTE": "b-success",
 "ACTIVE": "b-success",
 "A_JOUR": "b-success",
 "SOLDE": "b-success",
 "PAYE": "b-success",
 "VALIDE": "b-success",
 "EN_ATTENTE_VALIDATION": "b-warn",
 "BROUILLON": "b-warn",
 "PREPARATION": "b-warn",
 "DESACTIVEE": "b-warn",
 "ATTENDU": "b-warn",
 "PARTIEL": "b-warn",
 "DEPOSE": "b-warn",
 "EN_ATTENTE_PAIEMENT": "b-warn",
 "ANNULEE": "b-danger",
 "ANNULE": "b-danger",
 "ABANDON": "b-danger",
 "CLOTUREE": "b-danger",
 "PERDUE": "b-danger",
 "EXPIREE": "b-danger",
 "EN_RETARD": "b-danger",
 "REJETE": "b-danger"
}

export const SEXE_COLOR: Record<string, string> = {
 "MASCULIN": "var(--primary-dark)",
 "FEMININ": "var(--warn)"
}

export const ENTITY_DESCRIPTIONS: Record<string, string> = {
 "ecoles": "L'établissement lui-même : coordonnées, comptes bancaires et informations affichées sur les documents imprimés (reçus, relevés, attestations).",
 "annees": "Les années académiques successives (ex. 2025-2026). Marque celle en cours comme « courante », elle détermine les listes de sélection par défaut ailleurs dans l'app.",
 "periodes": "Les semestres de chaque année académique, rattachés individuellement à une année, ce qui permet de les distinguer d'une année sur l'autre.",
 "cycles": "Le regroupement administratif des niveaux (ex. « Premier cycle, Licence / BTS »), utilisé notamment pour filtrer les relances.",
 "offres": "Les filières proposées par l'école (ex. « Licence Marketing Management »), avec le diplôme préparé et la durée de la formation.",
 "niveaux": "Les niveaux d'études (ex. « 1ère année »), rattachés à un cycle, avec leur moyenne de passage.",
 "niveauOffre": "Le paramétrage propre à un niveau pour une offre donnée (libellé local, volume horaire, ordre dans le cursus).",
 "classes": "Les classes ouvertes chaque année, chacune rattachée à une offre, un niveau et une année académique précise, c'est elle qu'on choisit à l'inscription.",
 "etudiants": "Les fiches des étudiants, matricule généré automatiquement, avec accès à leurs inscriptions, paiements, notes et bulletins.",
 "enseignants": "Le personnel enseignant : spécialité et type de contrat.",
 "parents": "Les parents ou tuteurs, à relier aux étudiants (payeur, contact d'urgence) depuis l'onglet Parents de la fiche étudiant.",
 "personnelAdmin": "Le personnel administratif de l'école : fonction et service.",
 "liensParentaux": "Le détail des liens entre un étudiant et ses parents (type de lien, qui est payeur, ordre de priorité).",
 "inscriptions": "Les inscriptions des étudiants dans une classe, avec leur statut (En attente de paiement, Validée, Annulée).",
 "cartesEtudiantes": "Les cartes délivrées aux étudiants, avec leurs dates de validité.",
 "tarifications": "Les frais facturés (scolarité, inscription, frais annexes…), avec leur montant, par niveau et par année académique, la base du calcul des échéanciers.",
 "tranches": "Le calendrier des échéances de paiement (ex. Octobre, Novembre…) pour une année donnée.",
 "reductions": "Les barèmes de réduction disponibles (bourse, fratrie…), à appliquer ensuite au cas par cas sur un dossier.",
 "reductionsAppliquees": "L'historique des réductions effectivement accordées à chaque inscription.",
 "versements": "L'historique des paiements enregistrés. Le bouton « + Nouveau » ouvre un formulaire simplifié : cherche l'étudiant, le montant se pré-remplit avec la prochaine échéance.",
 "recus": "Les reçus générés automatiquement à chaque versement, avec leur numéro et la possibilité de les réimprimer en PDF.",
 "unitesEnseignement": "Les UE regroupent plusieurs matières (ECUE) et servent de base au calcul des moyennes de semestre sur le relevé de notes.",
 "matieres": "Les matières enseignées (ECUE), chacune rattachée à une UE, avec son coefficient et ses crédits.",
 "notes": "Les notes des étudiants, matière par matière et par période, elles alimentent automatiquement le relevé de notes.",
 "bulletins": "Le suivi des bulletins par classe et par période, avec moyenne, rang et appréciation.",
 "utilisateurs": "Les comptes permettant d'accéder à l'application.",
 "roles": "Les rôles applicatifs, qui regroupent un ensemble de permissions.",
 "permissions": "Le détail des droits d'accès pouvant être attribués à un rôle, module par module.",
 "utilisateurRoles": "Le rattachement de chaque utilisateur à un ou plusieurs rôles, école par école."
}
